<template>
    <div class="ds-employees-pager">
        <span class="ds-employees-pager-range">{{ rangeText }}</span>
        <div class="ds-employees-pager-size" v-click-outside="closeSizeMenu" @keydown.esc.stop="closeSizeMenuAndFocus">
            <button
                ref="sizeTrigger"
                type="button"
                class="ds-employees-pager-size-trigger"
                :class="{ 'is-open': isSizeMenuOpen }"
                aria-haspopup="menu"
                :aria-expanded="isSizeMenuOpen"
                aria-label="Количество строк на странице"
                @click="isSizeMenuOpen = !isSizeMenuOpen"
            >
                {{ sizeLabel(pageSize) }}
                <svg class="ds-employees-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
            </button>
            <div v-if="isSizeMenuOpen" class="ds-glass-menu ds-employees-pager-size-menu" role="menu">
                <button
                    v-for="option in sizeOptions"
                    :key="option"
                    type="button"
                    role="menuitemradio"
                    class="ds-glass-menu-item"
                    :class="{ 'is-selected': option === pageSize }"
                    :aria-checked="option === pageSize"
                    @click="selectSize(option)"
                >
                    <span class="ds-glass-menu-item-label">{{ sizeLabel(option) }}</span>
                </button>
            </div>
        </div>
        <nav v-if="pagesCount > 1" class="ds-employees-pager-pages" aria-label="Страницы">
            <button type="button" class="ds-employees-pager-arrow" aria-label="Назад" :disabled="page <= 1" @click="goTo(page - 1)">
                <icon width="9" height="13"><IconChevronLeft /></icon>
            </button>
            <button
                v-for="item in visiblePages"
                :key="item"
                type="button"
                class="ds-employees-pager-page"
                :class="{ 'is-active': item === page }"
                :aria-current="item === page ? 'page' : undefined"
                @click="goTo(item)"
            >
                {{ item }}
            </button>
            <button type="button" class="ds-employees-pager-arrow" aria-label="Вперёд" :disabled="page >= pagesCount" @click="goTo(page + 1)">
                <icon width="9" height="13"><IconChevronRight /></icon>
            </button>
        </nav>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import IconChevronLeft from '@/ui/icons/IconChevronLeft.vue'
import IconChevronRight from '@/ui/icons/IconChevronRight.vue'
import type { PageSize } from './employeesList'

const { total } = defineProps<{ total: number }>()

const page = defineModel<number>('page', { required: true })
const pageSize = defineModel<PageSize>('pageSize', { required: true })

const sizeOptions: PageSize[] = [15, 30]
const MAX_PAGES_SHOWN = 5

const isSizeMenuOpen = ref(false)
const sizeTrigger = ref<HTMLButtonElement | null>(null)

const sizeLabel = (size: PageSize) => `По ${size}`

const pagesCount = computed(() => {
    if (total <= 0) return 1
    return Math.ceil(total / pageSize.value)
})

const rangeText = computed(() => {
    if (total <= 0) return '0 из 0'
    const formattedTotal = total.toLocaleString('ru-RU')
    const from = (page.value - 1) * pageSize.value + 1
    const to = Math.min(page.value * pageSize.value, total)
    return `${from.toLocaleString('ru-RU')}–${to.toLocaleString('ru-RU')} из ${formattedTotal}`
})

const visiblePages = computed(() => {
    const count = pagesCount.value
    const half = Math.floor(MAX_PAGES_SHOWN / 2)
    let start = Math.max(1, page.value - half)
    const end = Math.min(count, start + MAX_PAGES_SHOWN - 1)
    start = Math.max(1, end - MAX_PAGES_SHOWN + 1)
    return Array.from({ length: end - start + 1 }, (_, index) => start + index)
})

const goTo = (value: number) => {
    if (value < 1 || value > pagesCount.value || value === page.value) return
    page.value = value
}

const selectSize = (size: PageSize) => {
    isSizeMenuOpen.value = false
    if (size !== pageSize.value) pageSize.value = size
}

const closeSizeMenu = () => {
    isSizeMenuOpen.value = false
}

const closeSizeMenuAndFocus = () => {
    if (!isSizeMenuOpen.value) return
    closeSizeMenu()
    sizeTrigger.value?.focus()
}
</script>
